const DESKTOP_LINK_REDIRECT_STORAGE_KEY = "qflow_desktop_link_redirect";
const DESKTOP_LINK_REDIRECT_MAX_AGE_MS = 15 * 60 * 1000;
const WINDOW_NAME_PREFIX = "__qflow_desktop_link_redirect__:";

function isSafeDesktopLinkRedirect(value) {
  return typeof value === "string" && value.startsWith("/desktop-link");
}

export function buildDesktopSignupRedirect(locationLike) {
  const hash = String(locationLike?.hash || "").replace(/^#/, "");
  const params = new URLSearchParams(hash);
  const redirectUri = params.get("desktop_redirect_uri");
  if (!redirectUri) return "";

  try {
    const parsed = new URL(redirectUri);
    if (!["http:", "https:"].includes(parsed.protocol)) return "";
    if (!["127.0.0.1", "localhost"].includes(parsed.hostname)) return "";
  } catch {
    return "";
  }

  const safeParams = new URLSearchParams();
  [
    "desktop_redirect_uri",
    "desktop_api_base_url",
    "desktop_auth_intent",
    "desktop_target_profile_key",
    "desktop_target_customer_id",
    "desktop_target_user_id",
    "desktop_target_company_name",
    "desktop_instance_id",
    "journey_id",
    "browser_id",
    "desktop_link_mode",
    "release_version",
    "platform",
    "arch",
    "source",
    "campaign_source",
    "campaign_medium",
    "campaign_name",
  ].forEach((name) => {
    const value = params.get(name);
    if (value) safeParams.set(name, value);
  });
  return `/desktop-link#${safeParams.toString()}`;
}

function buildRedirectRecord(redirect) {
  return JSON.stringify({
    redirect,
    saved_at: Date.now(),
  });
}

function parseRedirectRecord(raw) {
  if (!raw) {
    return "";
  }
  try {
    const parsed = JSON.parse(raw);
    const redirect = parsed?.redirect;
    const savedAt = Number(parsed?.saved_at || 0);
    const isFresh = savedAt > 0 && Date.now() - savedAt <= DESKTOP_LINK_REDIRECT_MAX_AGE_MS;
    return isFresh && isSafeDesktopLinkRedirect(redirect) ? redirect : "";
  } catch {
    return "";
  }
}

function writeWindowNameRedirect(record) {
  try {
    window.name = `${WINDOW_NAME_PREFIX}${record}`;
  } catch {
    // Best effort only; local/session storage are the primary stores.
  }
}

function readWindowNameRedirect() {
  try {
    const name = String(window.name || "");
    if (!name.startsWith(WINDOW_NAME_PREFIX)) {
      return "";
    }
    return parseRedirectRecord(name.slice(WINDOW_NAME_PREFIX.length));
  } catch {
    return "";
  }
}

function clearWindowNameRedirect() {
  try {
    if (String(window.name || "").startsWith(WINDOW_NAME_PREFIX)) {
      window.name = "";
    }
  } catch {
    // Nothing to clear.
  }
}

export function saveDesktopLinkRedirect(redirect) {
  if (!isSafeDesktopLinkRedirect(redirect) || typeof window === "undefined") {
    return false;
  }
  const record = buildRedirectRecord(redirect);
  window.localStorage.setItem(DESKTOP_LINK_REDIRECT_STORAGE_KEY, record);
  window.sessionStorage.setItem(DESKTOP_LINK_REDIRECT_STORAGE_KEY, record);
  writeWindowNameRedirect(record);
  return true;
}

export function getDesktopLinkRedirect() {
  if (typeof window === "undefined") {
    return "";
  }
  return parseRedirectRecord(window.sessionStorage.getItem(DESKTOP_LINK_REDIRECT_STORAGE_KEY))
    || parseRedirectRecord(window.localStorage.getItem(DESKTOP_LINK_REDIRECT_STORAGE_KEY))
    || readWindowNameRedirect();
}

export function getPostAuthenticationRedirect(fallback = "/") {
  return getDesktopLinkRedirect() || fallback;
}

export function navigateAfterAuthentication(history, fallback = "/") {
  const destination = getPostAuthenticationRedirect(fallback);
  history.replace(destination);
  return destination;
}

export function clearDesktopLinkRedirect() {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(DESKTOP_LINK_REDIRECT_STORAGE_KEY);
  window.sessionStorage.removeItem(DESKTOP_LINK_REDIRECT_STORAGE_KEY);
  clearWindowNameRedirect();
}

export function hasDesktopLinkRedirect() {
  return Boolean(getDesktopLinkRedirect());
}

export function captureDesktopSignupRedirect(locationLike) {
  const redirect = buildDesktopSignupRedirect(locationLike);
  return redirect ? saveDesktopLinkRedirect(redirect) : false;
}
